/**
 * Ecran charge a la demande (`lazy`) : attente pendant le telechargement de
 * son morceau, puis limite d'erreur. Apres un redeploiement, le morceau demande
 * par un onglet ouvert plus tot n'existe plus sur le serveur ; sans limite,
 * son echec (comme une exception de rendu) laisserait un ecran blanc.
 */
import { Component, Suspense } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';
import { BlocChargement, Bouton, EtatVide } from '../composants/ui';

function Attente() {
  return (
    <div className="panneau p-4">
      <BlocChargement lignes={6} libelle="Chargement de l'écran…" />
    </div>
  );
}

function EcranEnErreur() {
  return (
    <div role="alert" className="panneau">
      <EtatVide
        icone={<TriangleAlert />}
        baliseTitre="h1"
        titre="Cet écran n'a pas pu s'afficher"
        description="L'application a peut-être été mise à jour depuis l'ouverture de cette page. Rechargez-la pour continuer."
        action={
          <Bouton variante="primaire" onClick={() => window.location.reload()}>
            Recharger la page
          </Bouton>
        }
      />
    </div>
  );
}

interface ProprietesLimite {
  /** Adresse affichee : en changer retente le rendu au lieu de garder l'erreur. */
  cle: string;
  children: ReactNode;
}

class LimiteErreurEcran extends Component<ProprietesLimite, { enErreur: boolean }> {
  override state = { enErreur: false };

  static getDerivedStateFromError(): { enErreur: boolean } {
    return { enErreur: true };
  }

  // Les routes reutilisent la meme limite d'un ecran a l'autre : sans remise a
  // zero, l'erreur d'un ecran masquerait le suivant.
  override componentDidUpdate(precedentes: ProprietesLimite): void {
    if (this.state.enErreur && precedentes.cle !== this.props.cle) this.setState({ enErreur: false });
  }

  override render(): ReactNode {
    return this.state.enErreur ? <EcranEnErreur /> : this.props.children;
  }
}

export function Differe({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <LimiteErreurEcran cle={pathname}>
      <Suspense fallback={<Attente />}>{children}</Suspense>
    </LimiteErreurEcran>
  );
}
